'use client'
import { useRef, useEffect } from 'react'

/**
 * Wraps children in a container that fades + slides in when scrolled into view.
 * Uses IntersectionObserver with direct DOM class mutation — no useState, no re-renders.
 *
 * Props:
 *   delay   — ms before transition starts (for staggered siblings)
 *   as      — tag to render (default 'div')
 *   className — extra classes appended to the wrapper
 */
export default function Reveal({ children, delay = 0, as: Tag = 'div', className }) {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (delay) el.style.transitionDelay = `${delay}ms`

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('in-view')
          obs.disconnect()
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -52px 0px' },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [delay])

  const cls = ['reveal', className].filter(Boolean).join(' ')
  return <Tag ref={ref} className={cls}>{children}</Tag>
}
